import React from "react";
import styled, { css } from "styled-components";
import { style } from "../style";
import { focusOutline } from "../utils/focusOutlineStyle";
import { CopyButton, ClipboardButton } from "./CopyButton";

// number of characters before gradient appears
const MAX_LENGTH = 33;

const Wrapper = styled.div<StyledProps>`
  display: ${({ stretch }) => (stretch ? "flex" : "inline-flex")};
  align-items: center;
  max-width: 100%;
`;

const CopyLabel = styled.span`
  padding-right: ${style.vars.space["3"]};
  color: ${style.colors.grey.darkest};
`;

export const CopyWrapper = styled.div<StyledProps>`
  background: ${({ inline }) => (inline ? "none" : style.colors.grey.lightest)};
  height: ${({ inline, compact }) => (inline ? "auto" : compact ? style.vars.height.small : style.vars.height.base)};
  display: flex;
  max-width: 100%;

  ${({ inline }) =>
    inline &&
    css`
      align-items: center;
    `};

  ${({ stretch }) =>
    stretch &&
    css`
      width: 100%;
    `}

  &:focus {
    ${focusOutline()};
  }

  ${ClipboardButton} {
    height: ${({ inline }) => (inline ? "auto" : "100%")};
    width: ${({ inline, compact }) => (inline ? "auto" : compact ? "40px" : "50px")};
    border: ${({ inline }) => (inline ? "none" : `1px solid ${style.colors.grey.light}`)};
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    background-color: ${({ inline }) => (inline ? "transparent" : "white")};

    ${({ stretch }) =>
      stretch &&
      css`
        flex-shrink: 0;
      `}

    ${({ inline }) =>
      inline &&
      css`
        line-height: 1;
        padding: 0;
        position: relative;
        top: -2px;
      `}

    ${({ isCopying, inline }) =>
      !isCopying &&
      css`
        &:hover {
          background-color: ${inline ? "transparent" : style.colors.grey.light};
        }
      `}
  }
`;

export const CopyText = styled.pre<StyledProps>`
  position: relative;
  color: ${style.colors.grey.darkest};
  background: none;
  font-size: ${style.vars.fontSize.base};
  font-family: ${({ inline }) => (inline ? "inherit" : "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace")};
  text-align: left;
  white-space: pre;
  word-spacing: normal;
  word-break: normal;
  word-wrap: normal;
  line-height: 1.5;
  padding-top: 0;
  padding-right: ${({ compact }) => (compact ? style.vars.space["2"] : style.vars.space["3"])};
  padding-bottom: 0;
  padding-left: ${({ compact, inline }) => (inline ? 0 : compact ? style.vars.space["2"] : style.vars.space["3"])};
  margin-bottom: 0;
  z-index: ${style.vars.zIndex.base};
  max-width: ${({ fitToText, stretch }) => (fitToText || stretch ? "inherit" : "350px")};
  overflow: hidden;
  height: 100%;
  display: inline-flex;
  align-items: center;
  border: ${({ inline }) => (inline ? "none" : `1px solid ${style.colors.grey.light}`)};
  border-top-left-radius: ${style.vars.borderRadius.base};
  border-bottom-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;

  ${({ gradient, inline }) =>
    gradient &&
    !inline &&
    css`
      &::after {
        content: "";
        position: absolute;
        display: block;
        right: 0;
        top: 0;
        height: 100%;
        width: 100px;
        background: linear-gradient(to left, ${style.colors.grey.lightest} 0%, transparent 100%);
      }
    `}

  ${({ stretch }) =>
    stretch &&
    css`
      flex-grow: 1;
    `}
`;

type StyledProps = {
  gradient?: boolean;
  compact?: boolean;
  inline?: boolean;
  fitToText?: boolean;
  stretch?: boolean;
  isCopying?: boolean;
};

export type Props = {
  copy: string;
  displayCopyText?: string;
  tooltipText?: string;
  noTooltip?: boolean;
  onClick?: () => void;
  variation?: "compact" | "inline" | "fitToText" | "stretch";
  ariaLabel?: string;
  label?: string;
  className?: string;
};

export const ClickToCopy: React.FC<Props> = ({
  copy,
  displayCopyText,
  tooltipText,
  noTooltip = false,
  ariaLabel,
  onClick,
  variation,
  label,
  className,
}) => {
  const isInline = variation === "inline";
  const isCompact = variation === "compact";
  const isFitToText = variation === "fitToText";
  const isStretch = variation === "stretch";
  const hasGradient = (displayCopyText || copy)?.length >= MAX_LENGTH && (!isFitToText || !isStretch);

  const copyButton = <CopyButton copy={copy} onClick={onClick} noTooltip={noTooltip} tooltipText={tooltipText} ariaLabel={ariaLabel} />;

  return (
    <Wrapper className={className} stretch={isStretch}>
      {!isInline && label && <CopyLabel>{label}</CopyLabel>}
      <CopyWrapper inline={isInline} compact={isInline || isCompact} stretch={isStretch} role="figure">
        <CopyText inline={isInline} compact={isInline || isCompact} fitToText={isFitToText} stretch={isStretch} gradient={hasGradient}>
          {displayCopyText || copy}
        </CopyText>
        {copyButton}
      </CopyWrapper>
    </Wrapper>
  );
};
